import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { BlogCard } from "../components/BlogCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resilient GET: timeout + automatic retries (recovers from transient hangs on production).
const getWithRetry = async (url, config = {}, tries = 3) => {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try {
      return await axios.get(url, { timeout: 12000, ...config });
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr;
};

export default function BlogIndex() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("categoria") || "Tutti");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setFilter(searchParams.get("categoria") || "Tutti");
  }, [searchParams]);

  const selectFilter = (c) => {
    setFilter(c);
    if (c === "Tutti") setSearchParams({});
    else setSearchParams({ categoria: c });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getWithRetry(`${API}/blog/categories`).then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(false);
    let cancelled = false;
    const params = {};
    if (filter !== "Tutti") params.category = filter;
    if (search.trim()) params.search = search.trim();
    const t = setTimeout(() => {
      getWithRetry(`${API}/blog`, { params })
        .then((r) => { if (!cancelled) { setArticles(r.data); setError(false); } })
        .catch(() => { if (!cancelled) setError(true); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [filter, search, reloadKey]);

  return (
    <div data-testid="blog-index-page">
      <Seo
        title="Centro Risorse VEPA e Pergole Bioclimatiche | VETRO DESIGN ITALIA"
        description="Guide, normativa e realizzazioni su vetrate panoramiche VEPA e pergole bioclimatiche nelle Marche. Il blog di VETRO DESIGN ITALIA."
        path="/blog"
      />
      <Navbar />
      <PageHeader
        eyebrow="Centro Risorse"
        h1="Blog: guide, normativa e consigli sulle VEPA"
        intro="Approfondimenti su vetrate panoramiche VEPA e pergole bioclimatiche: costi, permessi, normativa e consigli pratici per valorizzare i tuoi spazi nelle Marche."
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <nav aria-label="breadcrumb" className="text-xs text-foreground/50 mb-10 text-center" data-testid="blog-breadcrumb">
          <a href="/" className="link-underline">Home</a> <span className="mx-2">/</span> <span className="text-foreground">Blog</span>
        </nav>

        <div className="flex flex-col items-center gap-6 mb-14">
          <div className="relative w-full max-w-md">
            <Search strokeWidth={1.2} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
            <input
              data-testid="blog-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca un articolo..."
              className="w-full border border-black/15 bg-white pl-11 pr-4 py-3 text-sm font-light focus:outline-none focus:border-black transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {["Tutti", ...categories].map((c) => (
              <button
                key={c}
                data-testid={`blog-filter-${c}`}
                onClick={() => selectFilter(c)}
                className={`text-xs uppercase tracking-wide px-4 py-2 border transition-colors duration-300 ${
                  filter === c ? "bg-black text-white border-black" : "border-black/15 hover:border-black"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-center text-foreground/50 py-16">Caricamento...</p>
        ) : error ? (
          <div className="text-center py-16" data-testid="blog-error">
            <p className="text-foreground/60 mb-5">Non è stato possibile caricare gli articoli. Controlla la connessione e riprova.</p>
            <button
              data-testid="blog-retry-button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="text-xs uppercase tracking-wide px-6 py-3 border border-black hover:bg-black hover:text-white transition-colors"
            >
              Riprova
            </button>
          </div>
        ) : articles.length === 0 ? (
          <p data-testid="blog-empty" className="text-center text-foreground/50 py-16">
            Nessun articolo trovato.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {articles.map((a) => (
              <BlogCard key={a.slug} article={a} />
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
